import type { PartialBlock } from '@blocknote/core';
import { createReactBlockSpec } from '@blocknote/react';

import { dropdownBlockPropSchema } from './dropdown-block-config';
import { DropdownBlockRenderer } from './dropdown-block-renderer';

/**
 * Custom BlockNote block that renders a dropdown/select component
 * Users can select a value from a predefined list of options.
 *
 * createReactBlockSpec returns a factory function that creates the BlockSpec.
 * We call it once to get the concrete spec object.
 */
const createDropdownBlockSpec = createReactBlockSpec(
  {
    type: 'dropdown',
    propSchema: dropdownBlockPropSchema,
    content: 'none', // Dropdown blocks don't need editable content
  },
  {
    // BlockNote's render function is a React component; the renderer owns all of
    // the hook logic and orchestration (see dropdown-block-renderer.tsx).
    render: (props) => <DropdownBlockRenderer {...props} />,
    // Parse HTML to convert back to block (optional, for copy/paste support)
    parse: (element: HTMLElement) => {
      const select = element.querySelector('select');
      if (select) {
        return {
          value: select.value,
          options: select.getAttribute('data-options') || '[]',
          label: select.getAttribute('data-label') || 'Select an option',
        };
      }
      return undefined;
    },
  },
);

// Export the concrete spec object (call the factory once)
export const dropdownBlockSpec = createDropdownBlockSpec();

// Backwards-compatible alias (kept for existing imports)
export const createDropdownBlock = dropdownBlockSpec;

/**
 * Helper function to create a dropdown block with predefined options
 */
export function createDropdownBlockWithOptions(
  options: Array<{ label: string; value: string; className?: string }>,
  label = 'Select an option',
  defaultValue = '',
): PartialBlock {
  return {
    id: crypto.randomUUID?.() || `dropdown-${Date.now()}-${Math.random()}`,
    type: 'dropdown' as const,
    props: {
      value: defaultValue,
      options: JSON.stringify(options),
      label,
    },
  } as unknown as PartialBlock;
}
